/**
 * Art for the props the wider world brought in: willows and cherry trees,
 * gates, lanterns, the windmill, city street furniture, boats, and the
 * teahouse furniture. Same light model and vocabulary as props.ts.
 */
import { footprint, type Prop } from '../../../shared/world.ts'
import { mix, shade } from './color.ts'
import { blobShadow, box, castShadow, cyl, iso, poly, rnd, type Pt } from './iso.ts'

type Ctx = CanvasRenderingContext2D

function sphere(ctx: Ctx, p: Pt, r: number, color: string): void {
  const g = ctx.createRadialGradient(p.x - r * 0.35, p.y - r * 0.4, r * 0.1, p.x, p.y, r)
  g.addColorStop(0, shade(color, 0.22)); g.addColorStop(0.7, color); g.addColorStop(1, shade(color, -0.16))
  ctx.fillStyle = g
  ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, Math.PI * 2); ctx.fill()
}
const faceY = (y: number, x0: number, x1: number, z0: number, z1: number): Pt[] => [iso(x0, y, z0), iso(x1, y, z0), iso(x1, y, z1), iso(x0, y, z1)]
const faceX = (x: number, y0: number, y1: number, z0: number, z1: number): Pt[] => [iso(x, y0, z0), iso(x, y1, z0), iso(x, y1, z1), iso(x, y0, z1)]

const SAKURA = ['#ffc6d9', '#ffb3cc', '#ffd6e5', '#f7a8c4', '#ffe0ec']
const CROP: Record<string, [string, string]> = { wheat: ['#e7c35a', '#c9a23a'], cabbage: ['#8fd16a', '#5fa84a'], pumpkin: ['#f08a2c', '#5fa84a'], sunflower: ['#ffd23f', '#5fa84a'] }

/** Draws `p` if it is one of the kinds this module owns. Returns false otherwise. */
export function drawExtra(ctx: Ctx, p: Prop, t: number, day: number, seed: number): boolean {
  const { w, d } = footprint(p)
  const x = p.x, y = p.y
  const cx = x + w / 2, cy = y + d / 2
  const night = 1 - day
  switch (p.kind) {
    case 'willow': {
      blobShadow(ctx, cx + 0.15, cy + 0.15, 0.7, 0.22)
      cyl(ctx, cx, cy, 0, 0.12, 1.5, '#8a6a4a')
      const top = iso(cx, cy, 2.2)
      sphere(ctx, { x: top.x, y: top.y - 6 }, 20, '#8fcf6a')
      ctx.lineCap = 'round'
      for (let i = 0; i < 16; i++) {
        const a = (i / 16) * Math.PI * 2
        const sx = top.x + Math.cos(a) * 17, sy = top.y - 4 + Math.sin(a) * 8
        const len = 26 + rnd(i, seed) * 18
        ctx.strokeStyle = i % 3 ? '#9ad673' : '#7cbf5a'; ctx.lineWidth = 3
        ctx.beginPath(); ctx.moveTo(sx, sy); ctx.quadraticCurveTo(sx + Math.cos(a) * 6, sy + len * 0.5, sx + Math.cos(a) * 4, sy + len); ctx.stroke()
      }
      return true
    }
    case 'sakura': {
      blobShadow(ctx, cx + 0.15, cy + 0.15, 0.62, 0.22)
      cyl(ctx, cx, cy, 0, 0.11, 1.2, '#6b4a3a')
      const base = iso(cx, cy, 1.85)
      ctx.strokeStyle = '#6b4a3a'; ctx.lineWidth = 3
      ctx.beginPath(); ctx.moveTo(base.x, base.y + 14); ctx.lineTo(base.x - 10, base.y - 4); ctx.moveTo(base.x, base.y + 14); ctx.lineTo(base.x + 9, base.y - 2); ctx.stroke()
      for (const [dx, dy, r, c] of [[-14, 2, 15, 0], [14, 1, 14, 1], [0, -9, 18, 2], [-7, -20, 12, 3], [9, -19, 12, 4], [0, 9, 13, 1]] as const) {
        sphere(ctx, { x: base.x + dx, y: base.y + dy }, r, SAKURA[(c + seed) % SAKURA.length]!)
      }
      ctx.fillStyle = '#ffffff'
      for (let i = 0; i < 10; i++) ctx.fillRect(base.x - 20 + rnd(i, seed, 1) * 40, base.y - 26 + rnd(i, seed, 2) * 40, 1.6, 1.6)
      return true
    }
    case 'bamboo': {
      for (let i = 0; i < 5; i++) {
        const bx = cx - 0.3 + rnd(i, seed) * 0.6, by = cy - 0.3 + rnd(seed, i) * 0.6
        const h = 2.2 + rnd(i, seed, 3) * 1.3
        const a = iso(bx, by, 0), b = iso(bx, by, h)
        ctx.strokeStyle = i % 2 ? '#6fb85a' : '#89c96a'; ctx.lineWidth = 3.4
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke()
        ctx.strokeStyle = 'rgba(40,80,30,0.45)'; ctx.lineWidth = 3.6
        for (let k = 0.5; k < h; k += 0.55) { const q = iso(bx, by, k); ctx.beginPath(); ctx.moveTo(q.x - 1.8, q.y); ctx.lineTo(q.x + 1.8, q.y); ctx.stroke() }
        ctx.fillStyle = '#7cc764'
        for (let k = 0; k < 3; k++) { ctx.beginPath(); ctx.ellipse(b.x + (k - 1) * 6, b.y + k * 4, 7, 2.4, (k - 1) * 0.6, 0, Math.PI * 2); ctx.fill() }
      }
      return true
    }
    case 'rock': {
      blobShadow(ctx, cx, cy, 0.45, 0.22)
      const b = iso(cx, cy, 0.18)
      sphere(ctx, { x: b.x, y: b.y - 2 }, 13 + (seed % 3) * 2, '#a9a39b')
      sphere(ctx, { x: b.x + 9, y: b.y + 3 }, 8, '#9a948c')
      return true
    }
    case 'hay': {
      castShadow(ctx, x + 0.15, y + 0.2, 0.7, 0.6, 0.5, 0.12)
      cyl(ctx, cx, cy, 0, 0.36, 0.5, '#e6c35c', { top: '#f0d47a' })
      ctx.strokeStyle = 'rgba(140,100,30,0.4)'; ctx.lineWidth = 1
      const b = iso(cx, cy, 0.25)
      ctx.beginPath(); ctx.ellipse(b.x, b.y, 23, 11, 0, 0.2, Math.PI - 0.2); ctx.stroke()
      return true
    }
    case 'crop': {
      const [c, leaf] = CROP[p.variant ?? 'wheat'] ?? CROP.wheat!
      for (let i = 0; i < 3; i++) {
        const q = iso(x + 0.25 + i * 0.25, y + 0.5, 0)
        if (p.variant === 'pumpkin') {
          ctx.fillStyle = leaf; ctx.beginPath(); ctx.ellipse(q.x, q.y - 2, 6, 3, 0, 0, Math.PI * 2); ctx.fill()
          if (i !== 1) sphere(ctx, { x: q.x, y: q.y - 4 }, 5.5, c)
        } else if (p.variant === 'cabbage') {
          sphere(ctx, { x: q.x, y: q.y - 4 }, 5.5, c)
        } else if (p.variant === 'sunflower') {
          ctx.strokeStyle = leaf; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(q.x, q.y); ctx.lineTo(q.x, q.y - 22); ctx.stroke()
          ctx.fillStyle = c; ctx.beginPath(); ctx.arc(q.x, q.y - 24, 5.5, 0, Math.PI * 2); ctx.fill()
          ctx.fillStyle = '#6b4a2a'; ctx.beginPath(); ctx.arc(q.x, q.y - 24, 2.4, 0, Math.PI * 2); ctx.fill()
        } else {
          ctx.strokeStyle = c; ctx.lineWidth = 1.6
          for (let k = -2; k <= 2; k++) { ctx.beginPath(); ctx.moveTo(q.x + k * 1.6, q.y); ctx.lineTo(q.x + k * 2.4, q.y - 14 - (k % 2) * 3); ctx.stroke() }
          ctx.fillStyle = shade(c, 0.1); for (let k = -2; k <= 2; k++) { ctx.beginPath(); ctx.ellipse(q.x + k * 2.4, q.y - 15 - (k % 2) * 3, 1.6, 3, 0, 0, Math.PI * 2); ctx.fill() }
        }
      }
      return true
    }
    case 'statue': {
      const col = p.color ?? '#c9c3bb'
      castShadow(ctx, x + 0.1, y + 0.1, 0.8, 0.8, 1.6, 0.14)
      box(ctx, x + 0.12, y + 0.12, 0, 0.76, 0.76, 0.7, '#d8d0c4')
      const b = iso(cx, cy, 0.7)
      // A blob on a plinth, in stone (or gold).
      ctx.fillStyle = shade(col, -0.1)
      ctx.beginPath(); ctx.ellipse(b.x, b.y - 16, 17, 16, 0, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = col
      ctx.beginPath(); ctx.ellipse(b.x - 1, b.y - 17, 15, 14.5, 0, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = 'rgba(255,255,255,0.4)'; ctx.beginPath(); ctx.ellipse(b.x - 6, b.y - 23, 5, 3, -0.6, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = shade(col, -0.45); ctx.fillRect(b.x - 6, b.y - 20, 3, 6); ctx.fillRect(b.x + 3, b.y - 20, 3, 6)
      if (p.color === '#ffd166') for (let i = 0; i < 4; i++) {
        const k = (t * 0.7 + i / 4) % 1
        ctx.fillStyle = `rgba(255,240,170,${1 - k})`; ctx.fillRect(b.x - 18 + i * 12, b.y - 30 - k * 20, 2, 2)
      }
      return true
    }
    case 'gazebo': {
      blobShadow(ctx, cx, cy, 1.5, 0.16)
      cyl(ctx, cx, cy, 0, 1.35, 0.12, '#e8e0d4')
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2
        if (Math.sin(a) < -0.3) cyl(ctx, cx + Math.cos(a) * 1.2, cy + Math.sin(a) * 1.2, 0.12, 0.06, 1.7, '#ffffff')
      }
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2
        if (Math.sin(a) >= -0.3) cyl(ctx, cx + Math.cos(a) * 1.2, cy + Math.sin(a) * 1.2, 0.12, 0.06, 1.7, '#ffffff')
      }
      const rim = iso(cx, cy, 1.8), top = iso(cx, cy, 2.6)
      const col = p.color ?? '#5aa9c9'
      for (let i = 0; i < 12; i++) {
        const a0 = (i / 12) * Math.PI * 2, a1 = ((i + 1) / 12) * Math.PI * 2
        const p0 = { x: rim.x + Math.cos(a0) * 1.5 * 32, y: rim.y + Math.sin(a0) * 1.5 * 16 }
        const p1 = { x: rim.x + Math.cos(a1) * 1.5 * 32, y: rim.y + Math.sin(a1) * 1.5 * 16 }
        poly(ctx, [top, p0, p1], shade(i % 2 ? col : '#ffffff', Math.sin((a0 + a1) / 2 - 0.6) * 0.12))
      }
      return true
    }
    case 'windmill': {
      castShadow(ctx, x + 0.3, y + 0.3, w - 0.6, d - 0.6, 3.5, 0.18)
      const b0 = iso(cx, cy, 0)
      // A tapered tower.
      const tw0 = 1.1, tw1 = 0.55, H = 3.6
      poly(ctx, [iso(cx - tw0, cy + tw0, 0), iso(cx + tw0, cy + tw0, 0), iso(cx + tw1, cy + tw1, H), iso(cx - tw1, cy + tw1, H)], '#f2e6d0')
      poly(ctx, [iso(cx + tw0, cy - tw0, 0), iso(cx + tw0, cy + tw0, 0), iso(cx + tw1, cy + tw1, H), iso(cx + tw1, cy - tw1, H)], '#d9c9ae')
      poly(ctx, faceY(cy + tw0 * 0.8, cx - 0.25, cx + 0.25, 0, 1.1), '#8a5a3a')
      const cap = iso(cx, cy, H)
      ctx.fillStyle = '#b0503c'
      ctx.beginPath(); ctx.moveTo(cap.x - 26, cap.y + 6); ctx.lineTo(cap.x, cap.y - 24); ctx.lineTo(cap.x + 26, cap.y + 6); ctx.closePath(); ctx.fill()
      const hub = { x: cap.x - 4, y: cap.y + 10 }
      const a0 = t * 0.9
      for (let i = 0; i < 4; i++) {
        const a = a0 + (i * Math.PI) / 2
        const ex = hub.x + Math.cos(a) * 58, ey = hub.y + Math.sin(a) * 58
        const nx = -Math.sin(a) * 8, ny = Math.cos(a) * 8
        poly(ctx, [{ x: hub.x + Math.cos(a) * 10, y: hub.y + Math.sin(a) * 10 }, { x: ex, y: ey }, { x: ex + nx, y: ey + ny }, { x: hub.x + Math.cos(a) * 10 + nx, y: hub.y + Math.sin(a) * 10 + ny }], 'rgba(255,250,240,0.92)')
        ctx.strokeStyle = '#8a5a3a'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(hub.x, hub.y); ctx.lineTo(ex, ey); ctx.stroke()
      }
      ctx.fillStyle = '#5a3d2b'; ctx.beginPath(); ctx.arc(hub.x, hub.y, 4, 0, Math.PI * 2); ctx.fill()
      void b0
      return true
    }
    case 'tramstop': {
      castShadow(ctx, x, y + 0.2, w, 0.6, 1.8, 0.12)
      for (const px of [x + 0.15, x + w - 0.25]) box(ctx, px, y + 0.15, 0, 0.1, 0.1, 1.9, '#3a3552')
      box(ctx, x, y + 0.05, 1.9, w, 0.8, 0.12, '#4fc3a1')
      poly(ctx, faceY(y + 0.2, x + 0.2, x + w - 0.2, 0.4, 1.8), 'rgba(190,230,245,0.55)')
      box(ctx, x + 0.3, y + 0.3, 0.38, w - 0.6, 0.35, 0.08, '#b98559')
      const s = iso(x + w / 2, y + 0.05, 2.25)
      ctx.fillStyle = '#ffffff'; ctx.beginPath(); ctx.roundRect(s.x - 16, s.y - 9, 32, 14, 4); ctx.fill()
      ctx.fillStyle = '#2f8f74'; ctx.font = '900 9px Nunito, ui-rounded, system-ui, sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
      ctx.fillText('TRAM', s.x, s.y - 2)
      return true
    }
    case 'gate': return gate(ctx, p, t, night), true
    case 'stonelantern': {
      blobShadow(ctx, cx, cy, 0.3, 0.2)
      box(ctx, cx - 0.22, cy - 0.22, 0, 0.44, 0.44, 0.12, '#a9a39b')
      cyl(ctx, cx, cy, 0.12, 0.08, 0.55, '#b4ada3')
      box(ctx, cx - 0.2, cy - 0.2, 0.67, 0.4, 0.4, 0.3, '#bdb6ac')
      poly(ctx, faceY(cy + 0.2 + 0.002, cx - 0.1, cx + 0.1, 0.72, 0.92), mix('#4a4540', '#ffd98a', night))
      const r0 = iso(cx, cy, 0.97), tip = iso(cx, cy, 1.25)
      ctx.fillStyle = '#9a948c'; ctx.beginPath(); ctx.moveTo(r0.x - 17, r0.y + 3); ctx.lineTo(tip.x, tip.y); ctx.lineTo(r0.x + 17, r0.y + 3); ctx.closePath(); ctx.fill()
      return true
    }
    case 'lanternpost': {
      blobShadow(ctx, cx, cy, 0.25, 0.18)
      box(ctx, cx - 0.04, cy - 0.04, 0, 0.08, 0.08, 1.9, '#3a2a22')
      const arm = iso(cx, cy, 1.85), end = { x: arm.x + 14, y: arm.y + 4 }
      ctx.strokeStyle = '#3a2a22'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(arm.x, arm.y); ctx.lineTo(end.x, end.y); ctx.stroke()
      ctx.fillStyle = mix('#c0392b', '#ff6b4a', night)
      ctx.beginPath(); ctx.ellipse(end.x, end.y + 9, 6, 7.5, 0, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = '#ffd166'; ctx.fillRect(end.x - 3, end.y + 1, 6, 2); ctx.fillRect(end.x - 3, end.y + 16, 6, 2)
      ctx.strokeStyle = '#ffd166'; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(end.x, end.y + 18); ctx.lineTo(end.x, end.y + 24); ctx.stroke()
      return true
    }
    case 'streetlamp': {
      blobShadow(ctx, cx, cy, 0.25, 0.18)
      cyl(ctx, cx, cy, 0, 0.05, 2.9, '#5a5f73')
      const head = iso(cx, cy, 2.9)
      ctx.strokeStyle = '#5a5f73'; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(head.x, head.y); ctx.quadraticCurveTo(head.x + 4, head.y - 8, head.x + 16, head.y - 6); ctx.stroke()
      ctx.fillStyle = '#5a5f73'; ctx.beginPath(); ctx.ellipse(head.x + 17, head.y - 5, 8, 3, 0, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = mix('#e8ecf4', '#fff2c0', night); ctx.beginPath(); ctx.ellipse(head.x + 17, head.y - 3.5, 6, 2, 0, 0, Math.PI * 2); ctx.fill()
      return true
    }
    case 'trafficlight': {
      blobShadow(ctx, cx, cy, 0.22, 0.18)
      cyl(ctx, cx, cy, 0, 0.05, 2.2, '#3a3552')
      box(ctx, cx - 0.12, cy + 0.05, 1.75, 0.24, 0.18, 0.75, '#2d2a3e')
      const phase = Math.floor((t / 4) % 3)
      ;['#ff5d5d', '#ffd166', '#5ddf8f'].forEach((c, i) => {
        const q = iso(cx, cy + 0.24, 2.38 - i * 0.22)
        ctx.fillStyle = i === phase ? c : shade(c, -0.6)
        ctx.beginPath(); ctx.arc(q.x, q.y, 3, 0, Math.PI * 2); ctx.fill()
      })
      return true
    }
    case 'car': {
      const col = p.color ?? '#ff6b6b'
      castShadow(ctx, x + 0.05, y + 0.08, w - 0.1, d - 0.16, 0.6, 0.18)
      const along = w >= d
      const [lx, ly] = along ? [w - 0.1, d - 0.3] : [w - 0.3, d - 0.1]
      box(ctx, x + 0.05 + (along ? 0 : 0.1), y + 0.15 - (along ? 0 : 0.1), 0.12, lx, ly, 0.38, col)
      const [cx0, cy0, cw, cd] = along ? [x + 0.45, y + 0.2, w - 0.9, d - 0.4] : [x + 0.2, y + 0.45, w - 0.4, d - 0.9]
      box(ctx, cx0, cy0, 0.5, cw, cd, 0.32, shade(col, 0.1))
      poly(ctx, along ? faceY(cy0 + cd + 0.002, cx0 + 0.05, cx0 + cw - 0.05, 0.54, 0.78) : faceX(cx0 + cw + 0.002, cy0 + 0.05, cy0 + cd - 0.05, 0.54, 0.78), mix('#bfe6f5', '#3a4060', night * 0.6))
      const wheels: Array<[number, number]> = along ? [[x + 0.35, y + d - 0.15], [x + w - 0.45, y + d - 0.15]] : [[x + w - 0.15, y + 0.35], [x + w - 0.15, y + d - 0.45]]
      for (const [wx, wy] of wheels) {
        const q = iso(wx, wy, 0.12); ctx.fillStyle = '#2d2a3e'; ctx.beginPath(); ctx.ellipse(q.x, q.y, 5, 5, 0, 0, Math.PI * 2); ctx.fill()
      }
      return true
    }
    case 'hydrant': {
      blobShadow(ctx, cx, cy, 0.2, 0.18)
      cyl(ctx, cx, cy, 0, 0.13, 0.5, '#e0443a', { top: '#f06a5a' })
      cyl(ctx, cx, cy, 0.5, 0.08, 0.12, '#c0392b')
      return true
    }
    case 'boat': return drawBoat(ctx, cx, cy, w >= d ? 1 : -1, 0, t), true
    case 'lowtable': {
      blobShadow(ctx, cx, cy, 0.45, 0.2)
      box(ctx, x + 0.12, y + 0.12, 0.1, 0.76, 0.76, 0.18, '#5a3d2b')
      for (const [lx, ly] of [[0.15, 0.15], [0.75, 0.15], [0.15, 0.75], [0.75, 0.75]] as const) box(ctx, x + lx, y + ly, 0, 0.08, 0.08, 0.1, '#3a2a22', { edge: false })
      cyl(ctx, cx - 0.1, cy, 0.28, 0.1, 0.12, '#e8e0d4', { top: '#6a994e' })
      cyl(ctx, cx + 0.18, cy - 0.12, 0.28, 0.06, 0.07, '#f6efe0', { top: '#9bc27a' })
      return true
    }
    case 'cushion': {
      const col = p.color ?? '#d94a5a'
      box(ctx, x + 0.18, y + 0.18, 0, 0.64, 0.64, 0.12, col)
      const q = iso(cx, cy, 0.12); ctx.fillStyle = shade(col, 0.2); ctx.beginPath(); ctx.arc(q.x, q.y, 2, 0, Math.PI * 2); ctx.fill()
      return true
    }
    case 'stool': {
      blobShadow(ctx, cx, cy, 0.3, 0.18)
      cyl(ctx, cx, cy, 0, 0.2, 0.4, p.color ?? '#7a3b2e', { top: shade(p.color ?? '#7a3b2e', 0.15) })
      return true
    }
    case 'screen': {
      const col = p.color ?? '#f6efe0'
      castShadow(ctx, x, y, w, 0.3, 1.6, 0.1)
      const n = Math.round(w * 2)
      for (let i = 0; i < n; i++) {
        const u0 = x + (i / n) * w, u1 = x + ((i + 1) / n) * w
        const off = i % 2 ? 0.12 : 0
        poly(ctx, [iso(u0, y + 0.2 + off, 0), iso(u1, y + 0.32 - off, 0), iso(u1, y + 0.32 - off, 1.6), iso(u0, y + 0.2 + off, 1.6)], '#5a3d2b')
        poly(ctx, [iso(u0 + 0.05, y + 0.2 + off, 0.1), iso(u1 - 0.05, y + 0.32 - off, 0.1), iso(u1 - 0.05, y + 0.32 - off, 1.5), iso(u0 + 0.05, y + 0.2 + off, 1.5)], i % 2 ? shade(col, -0.06) : col)
        const m = iso((u0 + u1) / 2, y + 0.26, 0.9)
        ctx.fillStyle = 'rgba(200,120,140,0.5)'; ctx.beginPath(); ctx.arc(m.x, m.y, 3, 0, Math.PI * 2); ctx.fill()
      }
      return true
    }
    default:
      return false
  }
}

/** Torii, paifang, a neon arch, or a plain arch, spanning the footprint's long side. */
function gate(ctx: Ctx, p: Prop, t: number, night: number): void {
  const { w, d } = footprint(p)
  const alongY = d > w
  const mid = alongY ? p.x + w / 2 : p.y + d / 2
  const a = alongY ? [mid, p.y + 0.3] : [p.x + 0.3, mid]
  const b = alongY ? [mid, p.y + d - 0.3] : [p.x + w - 0.3, mid]
  const at = (u: number, z: number) => (alongY ? iso(mid, a[1]! + (b[1]! - a[1]!) * u, z) : iso(a[0]! + (b[0]! - a[0]!) * u, mid, z))
  const post = (u: number, col: string, H: number, r = 0.1) => {
    const [px, py] = alongY ? [mid, a[1]! + (b[1]! - a[1]!) * u] : [a[0]! + (b[0]! - a[0]!) * u, mid]
    cyl(ctx, px, py, 0, r, H, col)
  }
  const beam = (u0: number, u1: number, z0: number, z1: number, col: string) => poly(ctx, [at(u0, z0), at(u1, z0), at(u1, z1), at(u0, z1)], col)
  const v = p.variant ?? 'arch'
  if (v === 'torii') {
    const red = '#d9412f', H = 2.5
    post(0, red, H, 0.11); post(1, red, H, 0.11)
    beam(-0.12, 1.12, 2.05, 2.25, red)
    beam(-0.22, 1.22, 2.45, 2.62, '#2d2a3e')
    beam(-0.18, 1.18, 2.38, 2.48, red)
    beam(0.44, 0.56, 2.25, 2.45, red)
  } else if (v === 'paifang') {
    const red = '#a8322a', H = 2.7
    for (const u of [0, 0.33, 0.67, 1]) post(u, red, u === 0 || u === 1 ? H - 0.4 : H, 0.09)
    beam(-0.05, 1.05, 1.9, 2.15, red)
    beam(0.25, 0.75, 2.15, 2.6, '#2a3a5a')
    beam(-0.15, 1.15, 2.6, 2.8, '#2f3540')
    beam(0.2, 0.8, 2.8, 3.0, '#2f3540')
    const c = at(0.5, 2.38)
    ctx.save(); ctx.translate(c.x, c.y); ctx.transform(1, alongY ? -0.5 : 0.5, 0, 1, 0, 0)
    ctx.font = '900 11px "Noto Serif SC", serif'; ctx.fillStyle = '#ffd166'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
    ctx.fillText(p.text ?? '', 0, 0); ctx.restore()
    for (const u of [0.1, 0.9]) {
      const q = at(u, 1.75)
      ctx.fillStyle = mix('#c0392b', '#ff6b4a', night); ctx.beginPath(); ctx.ellipse(q.x, q.y, 5, 6.5, 0, 0, Math.PI * 2); ctx.fill()
    }
  } else if (v === 'neon') {
    const H = 2.8
    post(0, '#2d2a3e', H, 0.09); post(1, '#2d2a3e', H, 0.09)
    beam(-0.05, 1.05, 2.4, 2.95, '#2d2a3e')
    const c = at(0.5, 2.67)
    const flick = 0.85 + 0.15 * Math.sin(t * 11)
    ctx.save(); ctx.translate(c.x, c.y); ctx.transform(1, alongY ? -0.5 : 0.5, 0, 1, 0, 0)
    ctx.font = '900 12px Nunito, ui-rounded, system-ui, sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
    ctx.shadowColor = '#ff5dd8'; ctx.shadowBlur = 8 * night * flick
    ctx.fillStyle = mix('#ff9be8', '#ffffff', 0.3 * flick)
    ctx.fillText(p.text ?? '', 0, 0); ctx.restore()
  } else {
    post(0, '#f3ead6', 1.8); post(1, '#f3ead6', 1.8)
    beam(-0.05, 1.05, 1.75, 1.95, '#c9a24a')
  }
}

/** A wupeng boat with a little lantern. `dir` ±1 flips it; `bob` lifts it. */
export function drawBoat(ctx: Ctx, cx: number, cy: number, dir: 1 | -1, bob: number, t: number): void {
  const c = iso(cx, cy, -0.18)
  ctx.save()
  ctx.translate(c.x, c.y - bob + Math.sin(t * 1.6 + cx) * 1.2)
  ctx.scale(dir, 1)
  ctx.fillStyle = 'rgba(20,40,70,0.25)'; ctx.beginPath(); ctx.ellipse(0, 4, 34, 9, 0, 0, Math.PI * 2); ctx.fill()
  ctx.fillStyle = '#6b4a34'
  ctx.beginPath(); ctx.moveTo(-36, -6); ctx.quadraticCurveTo(-30, 8, 0, 8); ctx.quadraticCurveTo(30, 8, 38, -8); ctx.lineTo(30, -4); ctx.lineTo(-28, -2); ctx.closePath(); ctx.fill()
  ctx.fillStyle = '#8a6a4a'; ctx.beginPath(); ctx.moveTo(-28, -2); ctx.lineTo(30, -4); ctx.lineTo(28, -1); ctx.lineTo(-26, 1); ctx.closePath(); ctx.fill()
  // The black awning.
  ctx.fillStyle = '#2f3540'
  ctx.beginPath(); ctx.moveTo(-14, -2); ctx.quadraticCurveTo(-4, -22, 10, -3); ctx.closePath(); ctx.fill()
  ctx.fillStyle = 'rgba(255,255,255,0.12)'; ctx.beginPath(); ctx.moveTo(-10, -4); ctx.quadraticCurveTo(-4, -18, 2, -12); ctx.lineTo(-2, -6); ctx.closePath(); ctx.fill()
  ctx.fillStyle = '#e0443a'; ctx.beginPath(); ctx.ellipse(24, -14, 3.5, 4.5, 0, 0, Math.PI * 2); ctx.fill()
  ctx.strokeStyle = '#3a2a22'; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(24, -18); ctx.lineTo(24, -26); ctx.lineTo(20, -24); ctx.stroke()
  ctx.restore()
}
